/**
 * Types for the Recurring Builder drawer.
 *
 * The builder's internal state is a BuilderDraft (strings for every
 * input so a half-typed amount is valid), separate from the normalized
 * RecurringTemplate the parent page stores.
 *
 * builderUtils.ts converts between the two:
 *   createEmptyDraft()     -> BuilderDraft (New mode defaults)
 *   fromTemplate(template) -> BuilderDraft (Edit mode)
 *   toTemplate(draft, old) -> RecurringTemplate (on save)
 *   validateDraft(draft)   -> BuilderValidationErrors ({} == valid)
 */

import type {
  RecurringMode,
  RecurringStatus,
  RecurringTxnType,
  RecurringFrequency,
} from "../recurringTypes";

/** Create a new template vs edit an existing one. Decides title, submit label, Delete affordance. */
export type BuilderMode = "create" | "edit";

/** How the schedule stops firing. */
export type ScheduleEndType = "never" | "after" | "on";

/**
 * The full form state the builder tracks while the user edits.
 * Field names align with RecurringSchedule to make toTemplate() a
 * near one-to-one copy. Strings stay strings until save.
 */
export interface BuilderDraft {
  mode: BuilderMode;
  /** null while creating; the id of the template being edited otherwise. */
  editingTemplateId: string | null;

  // Section 1: Basics
  name: string;
  recurringMode: RecurringMode;
  status: RecurringStatus;

  // Section 2: Transaction details
  txnType: RecurringTxnType;
  /** Supplier id (expense/bill), customer id (invoice), null for journal/transfer. */
  partyId: string | null;
  /** Cached from the options list so the preview summary and save can read it without a lookup. */
  partyName: string;
  accountId: string | null;
  accountName: string;
  /** Raw string input ("4,200.00" or "4200"). Parsed on save. */
  amount: string;
  /** ISO currency code; defaults to CAD, will later read from company settings. */
  currency: string;
  memo: string;

  // Section 3: Schedule
  frequency: RecurringFrequency;
  /** "Every N" multiplier (default 1). Only emitted into the schedule when > 1. */
  interval: number;
  /** 0 (Sun) through 6 (Sat). Used by weekly / biweekly. */
  dayOfWeek: number;
  /** 1-31 or "last". Used by monthly / quarterly / semi_annually / annually. */
  dayOfMonth: number | "last";
  /** ISO date YYYY-MM-DD. */
  startDate: string;
  endType: ScheduleEndType;
  /** When endType is "after". Clamped to 1-999 on save. */
  maxOccurrences: number;
  /** When endType is "on". ISO date or null. */
  endDate: string | null;
}

/**
 * Per-field validation errors. Empty object means the draft is ready.
 * Each key maps to a human string shown inline under its field.
 */
export interface BuilderValidationErrors {
  name?: string;
  partyId?: string;
  accountId?: string;
  amount?: string;
  startDate?: string;
  dayOfMonth?: string;
  dayOfWeek?: string;
  maxOccurrences?: string;
  endDate?: string;
}

export type BuilderFieldKey = keyof BuilderValidationErrors;

/** Dropdown option shape. Section components receive these from parent context. */
export interface BuilderOption {
  id: string;
  name: string;
  secondary?: string;
}

/** Options the builder needs from parent, loaded via hooks (useSuppliers etc.). */
export interface BuilderContextOptions {
  suppliers: BuilderOption[];
  customers: BuilderOption[];
  accounts: BuilderOption[];
}

/** Weekday short labels, Sunday = 0. */
export const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** Weekday one-letter labels for compact pill rows. */
export const WEEKDAY_LETTER = ["S", "M", "T", "W", "T", "F", "S"] as const;

/** Month short labels. */
export const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/** Month full labels (for the DatePicker chip header). */
export const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/**
 * The subset of RecurringFrequency values the builder exposes as pills.
 * The schedule type supports more (semi_annually, custom); those are
 * reachable via API only in v1.
 */
export const BUILDER_FREQUENCIES: readonly RecurringFrequency[] = [
  "daily",
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
  "annually",
] as const;

/** Pretty label for each frequency pill. */
export const FREQUENCY_PILL_LABELS: Record<RecurringFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semi_annually: "Twice yearly",
  annually: "Yearly",
  custom: "Custom",
};